import { useEffect, useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { confirmSheet } from "@/components/ui/action-sheet";

export function PricingLogSection() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/settings/pricing-log").then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  return (
    <section data-testid="pricing-log-section" className="rounded-2xl border border-slate-800 p-5">
      <h2 className="text-base font-bold text-slate-100 md:text-lg">Pricing change log</h2>
      {rows && rows.length === 0 && <p className="mt-2 text-sm text-slate-500">No pricing edits yet.</p>}
      <div className="mt-3 max-h-[60vh] overflow-auto">
        <table className="w-full text-left text-xs" data-testid="pricing-log-table">
          <thead className="text-[10px] uppercase tracking-wide text-slate-500"><tr><th className="py-1">When</th><th>Who</th><th>What</th><th>Field</th><th>Old → New</th></tr></thead>
          <tbody>{(rows || []).map((r, i) => (
            <tr key={i} data-testid={`pricing-log-row-${i}`} className="border-t border-slate-800/60 align-top">
              <td className="whitespace-nowrap py-1.5 pr-2 text-slate-400">{new Date(r.at).toLocaleString()}</td>
              <td className="pr-2 text-slate-300">{r.user_name}</td>
              <td className="pr-2 text-slate-200">{r.what}: {r.name}</td>
              <td className="pr-2 font-mono text-slate-400">{r.field}</td>
              <td className="break-all font-mono text-slate-300"><span className="text-red-300/80">{r.old}</span> → <span className="text-emerald-300">{r.new}</span></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </section>
  );
}

export function BackupSection() {
  const file = useRef(null);
  const [busy, setBusy] = useState(false);
  const exportIt = async () => {
    try {
      const { data } = await api.get("/settings/pricing-export");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 1)], { type: "application/json" }));
      a.download = `viewlux-pricing-${data.exported_at.slice(0, 10)}.json`;
      a.click();
      toast.success("Pricing setup exported");
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const importIt = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const body = JSON.parse(await f.text());
      if (!(await confirmSheet({ title: `Import ${body.product_lines?.length || 0} product lines and settings from ${f.name}?`, message: "Matching names are overwritten; saved quotes don't change.", confirm: "Import", destructive: true }))) return;
      const { data } = await api.post("/settings/pricing-import", body);
      toast.success(`Imported ${data.product_lines} product lines, ${data.types} types, ${data.shapes} shapes`);
    } catch (e) {
      toast.error(e instanceof SyntaxError ? "This isn't a pricing setup file" : errorMessage(e));
    } finally {
      setBusy(false);
      if (file.current) file.current.value = "";
    }
  };
  return (
    <section data-testid="pricing-backup-section" className="rounded-2xl border border-slate-800 p-5">
      <h2 className="text-base font-bold text-slate-100 md:text-lg">Backup & move pricing</h2>
      <p className="mt-1 text-sm text-slate-400">Export the whole pricing setup (types, product lines, glass, extras, screens, shapes, multiplier and uplifts) as a file. Import it here or on live to restore it. Items are matched by name.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid="pricing-export-button" onClick={exportIt} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-amber-500/60"><Download className="h-4 w-4" /> Export</button>
        <button data-testid="pricing-import-button" disabled={busy} onClick={() => file.current?.click()} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-amber-500/60 disabled:opacity-50"><Upload className="h-4 w-4" /> Import</button>
        <input ref={file} data-testid="pricing-import-file" type="file" accept="application/json,.json" className="hidden" onChange={(e) => importIt(e.target.files?.[0])} />
      </div>
    </section>
  );
}
